import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth, HomeRedirect } from '@/contexts/AuthContext';
import { Spinner } from '@/components/primitives/Spinner';
import AppShell from '@/layouts/AppShell';
import LoginPage from '@/pages/LoginPage';
import OrgChooserPage from '@/pages/OrgChooserPage';
import NotFoundPage from '@/pages/NotFoundPage';
import DashboardPage from '@/pages/DashboardPage';
import FormatsPage from '@/pages/FormatsPage';
import CampaignsPage from '@/pages/CampaignsPage';
import CampaignDetailPage from '@/pages/CampaignDetailPage';
import CreativesPage from '@/pages/CreativesPage';
import CreativeEditorPage from '@/pages/CreativeEditorPage';
import CreativeEditorNeoPage from '@/pages/CreativeEditorNeoPage';
import CreativeRendersPage from '@/pages/CreativeRendersPage';
import AssetsPage from '@/pages/AssetsPage';
import AssetDetailPage from '@/pages/AssetDetailPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import RendersPage from '@/pages/RendersPage';
import { OrgRouteGuard } from '@/components/app/OrgRouteGuard';
import { LegacyFlatRedirect } from '@/components/app/LegacyFlatRedirect';
import { buildLoginPath } from '@/core/urlRouting';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:items-center aspectly:justify-center aspectly:bg-surface">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={buildLoginPath(returnTo)} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<HomeRedirect />} />

      <Route
        path="/choose-org"
        element={
          <ProtectedRoute>
            <OrgChooserPage />
          </ProtectedRoute>
        }
      />

      {/* Legacy flat URLs → /org/:orgId/… (pre-migration bookmarks) */}
      <Route path="/formats" element={<LegacyFlatRedirect />} />
      <Route path="/campaigns" element={<LegacyFlatRedirect />} />
      <Route path="/campaigns/:campaignId" element={<LegacyFlatRedirect />} />
      <Route path="/campaigns/:campaignId/creatives/:creativeId" element={<LegacyFlatRedirect />} />
      <Route path="/creatives" element={<LegacyFlatRedirect />} />
      <Route path="/creatives/:creativeId" element={<LegacyFlatRedirect />} />
      <Route path="/creatives/:creativeId/oldeditor" element={<LegacyFlatRedirect />} />
      <Route path="/creatives/:creativeId/renders" element={<LegacyFlatRedirect />} />
      <Route path="/creatives/:creativeId/renders/:renderId" element={<LegacyFlatRedirect />} />
      <Route path="/assets" element={<LegacyFlatRedirect />} />
      <Route path="/assets/:assetId" element={<LegacyFlatRedirect />} />
      <Route path="/renders" element={<LegacyFlatRedirect />} />
      <Route path="/renders/:renderId" element={<LegacyFlatRedirect />} />
      <Route path="/analytics" element={<LegacyFlatRedirect />} />
      <Route path="/organizations/:orgId/*" element={<LegacyFlatRedirect />} />

      <Route
        path="/org/:orgId"
        element={
          <ProtectedRoute>
            <OrgRouteGuard />
          </ProtectedRoute>
        }
      >
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />
          <Route path="formats" element={<FormatsPage />} />
          <Route path="campaigns" element={<CampaignsPage />} />
          <Route path="campaigns/:campaignId" element={<CampaignDetailPage />} />
          <Route path="creatives" element={<CreativesPage />} />
          <Route path="creatives/:creativeId/oldeditor" element={<CreativeEditorPage />} />
          <Route path="creatives/:creativeId/renders" element={<CreativeRendersPage />} />
          <Route path="creatives/:creativeId/renders/:renderId" element={<CreativeRendersPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="assets/:assetId" element={<AssetDetailPage />} />
          <Route path="renders" element={<RendersPage />} />
          <Route path="renders/:renderId" element={<RendersPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
        </Route>
        <Route path="creatives/:creativeId" element={<CreativeEditorNeoPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
